import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { STATUS_PALETTE, readableOn } from '../status-color';

/**
 * Chọn màu cột — APM-BOARD-j.
 *
 * ## Vì sao có ô "tự chọn"
 *
 * Cột chưa đặt màu **không** phải cột không màu: `statusTone` suy ra một màu ổn
 * định từ nhóm trạng thái và id, nên board và bảng vẫn có màu ngay từ đầu. Ô
 * đầu tiên của bảng này nói ra điều đó và cho quay lại — thiếu nó thì đặt màu
 * là một cánh cửa một chiều, và người dùng chỉ còn cách đoán xem màu cũ là gì.
 *
 * ## Dấu tích lấy màu chữ từ `readableOn`
 *
 * Không phải luôn trắng: dấu tích trắng trên ô `#33d391` là một dấu tích không
 * nhìn thấy — đúng lỗi mà `readableOn` sinh ra để chặn, chỉ ở quy mô nhỏ hơn.
 */
export function ColorPicker({
  value,
  onChange,
}: {
  /** `null` = chưa đặt, để `statusTone` tự suy. */
  readonly value: string | null;
  readonly onChange: (color: string | null) => void;
}): ReactNode {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap gap-1" role="group" aria-label={t('board.columnColor')}>
      <button
        type="button"
        aria-label={t('board.colorAuto')}
        aria-pressed={value === null}
        title={t('board.colorAuto')}
        onClick={() => onChange(null)}
        className={cn(
          'flex size-6 items-center justify-center rounded-control border border-dashed border-border-strong text-[10px] text-muted-foreground',
          value === null && 'ring-2 ring-primary ring-offset-1',
        )}
      >
        {value === null ? <Check aria-hidden className="size-3" /> : 'A'}
      </button>

      {STATUS_PALETTE.map((color) => (
        <button
          key={color}
          type="button"
          aria-label={color}
          aria-pressed={value === color}
          title={color}
          onClick={() => onChange(color)}
          style={{ backgroundColor: color, color: readableOn(color) }}
          className={cn(
            'flex size-6 items-center justify-center rounded-control',
            value === color && 'ring-2 ring-primary ring-offset-1',
          )}
        >
          {value === color ? <Check aria-hidden className="size-3" /> : null}
        </button>
      ))}
    </div>
  );
}
